// Documents API - Handles document management operations
import { Document, ResidentDocument } from '../types/document';
import { loadMockData } from '../mock';
import { isDemoWorkspace } from '../utils/apiHelpers';
import { ref, uploadBytes, getDownloadURL, listAll } from 'firebase/storage';
import { storage } from '@/firebase';

// Local pagination function
function paginateMockData<T>(
  data: T[],
  pageSize: number,
  currentPage: number = 1
): { data: T[]; total: number } {
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = data.slice(startIndex, endIndex);
  
  return {
    data: paginatedData,
    total: data.length,
  };
}

export interface DocumentsResponse {
  data: Document[];
  total: number;
  page: number;
  pageSize: number;
}

export interface DocumentsQueryParams {
  page: number;
  pageSize: number;
  workspaceId: string;
}

export interface DocumentsByOwnerParams extends DocumentsQueryParams {
  ownerId: string;
  ownerType: 'resident' | 'employee';
}

// New interfaces for folder structure management
export interface WorkspaceFolderStructure {
  workspaceId: string;
  workspaceSlug: string;
  language: string;
  foldersCreated: boolean;
}

export interface DocumentUploadParams {
  file: File;
  workspaceId: string;
  workspaceSlug: string;
  folder: string;
  subfolder?: string;
  ownerId?: string;
  ownerType?: 'resident' | 'employee';
  category?: string;
  description?: string;
  language?: string;
}

export interface FolderInfo {
  name: string;
  path: string;
  files: number;
  subfolders: string[];
}

// Folder structure constants
export const FIRST_LEVEL_FOLDERS = [
  'Administrator',
  'Properties', 
  'Resident',
  'Employee',
  'Vendor',
  'Asset',
  'Banking',
  'Finance, compliances and reports'
];

// Language mapping for folder names - matches workspace language type
type WorkspaceLanguage = 'en' | 'es' | 'pt';

const FOLDER_TRANSLATIONS: Record<WorkspaceLanguage, Record<string, string>> = {
  en: {
    administration: 'Administrator',
    properties: 'Properties',
    residents: 'Resident',
    employees: 'Employee',
    vendors: 'Vendor',
    assets: 'Asset',
    banking: 'Banking',
    finance: 'Finance, compliances and reports'
  },
  es: {
    administration: 'Administrador',
    properties: 'Propiedades',
    residents: 'Residente',
    employees: 'Empleado',
    vendors: 'Proveedor',
    assets: 'Activo',
    banking: 'Bancario',
    finance: 'Finanzas, cumplimientos e informes'
  },
  pt: {
    administration: 'Administrador',
    properties: 'Propriedades',
    residents: 'Residente',
    employees: 'Funcionário',
    vendors: 'Fornecedor',
    assets: 'Ativo',
    banking: 'Bancário',
    finance: 'Finanças, conformidades e relatórios'
  }
};

// Get localized folder names based on workspace language
export const getLocalizedFolderNames = (language: string): string[] => {
  // Workspace language is stored as 'en' | 'es' | 'pt' - no processing needed
  const translations = FOLDER_TRANSLATIONS[language as WorkspaceLanguage] || FOLDER_TRANSLATIONS.en;
  
  const folderNames = [
    translations.administration,
    translations.properties,
    translations.residents,
    translations.employees,
    translations.vendors,
    translations.assets,
    translations.banking,
    translations.finance
  ];
  
  return folderNames;
};

// Helper function to create a folder marker in Firebase Storage
const createFolderMarker = async (folderPath: string): Promise<void> => {
  const folderRef = ref(storage, `${folderPath}/.folder`);
  const folderMarker = new Blob([''], { type: 'text/plain' });
  await uploadBytes(folderRef, folderMarker);
};

// Check if folder structure exists for a workspace
export const checkWorkspaceFolderStructure = async (
  _workspaceId: string, 
  workspaceSlug: string
): Promise<boolean> => {
  try {
    const rootRef = ref(storage, workspaceSlug);
    const result = await listAll(rootRef);
    
    // Check if we have at least some first-level folders
    return result.prefixes.length > 0;
  } catch (error) {
    console.error('Error checking folder structure:', error);
    return false;
  }
};

// Create initial folder structure for a workspace
export const createWorkspaceFolderStructure = async (
  _workspaceId: string,
  workspaceSlug: string,
  language: string
): Promise<void> => {
  try {
    const localizedFolderNames = getLocalizedFolderNames(language);
    const rootPath = workspaceSlug;
    
    // Create first-level folders
    for (const folderName of localizedFolderNames) {
      const folderPath = `${rootPath}/${folderName}`;
      await createFolderMarker(folderPath);
    }
    
    // Create specific subfolders for Administrator
    const adminFolderName = localizedFolderNames[0]; // Administrator
    const onboardingPath = `${rootPath}/${adminFolderName}/Onboarding`;
    await createFolderMarker(onboardingPath);
  } catch (error) {
    console.error('❌ Error creating folder structure:', error);
    throw error;
  }
};

// Create dynamic subfolder (for apartment units, residents, employees, etc.)
export const createDynamicSubfolder = async (
  _workspaceId: string,
  workspaceSlug: string,
  parentFolder: string,
  subfolderName: string,
  language: string
): Promise<void> => {
  try {
    const localizedFolderNames = getLocalizedFolderNames(language);
    const rootPath = workspaceSlug;
    
    // Find the localized name for the parent folder
    const parentFolderIndex = FIRST_LEVEL_FOLDERS.indexOf(parentFolder);
    if (parentFolderIndex === -1) {
      throw new Error(`Invalid parent folder: ${parentFolder}`);
    }
    
    const localizedParentFolder = localizedFolderNames[parentFolderIndex];
    const subfolderPath = `${rootPath}/${localizedParentFolder}/${subfolderName}`;
    
    await createFolderMarker(subfolderPath);
    console.log(`Dynamic subfolder created: ${subfolderPath}`);
  } catch (error) {
    console.error('Error creating dynamic subfolder:', error);
    throw error;
  }
};

// Get folder contents
export const getFolderContents = async (
  _workspaceId: string,
  workspaceSlug: string,
  folderPath: string = ''
): Promise<FolderInfo[]> => {
  try {
    const fullPath = folderPath 
      ? `${workspaceSlug}/${folderPath}`
      : workspaceSlug;
    
    const folderRef = ref(storage, fullPath);
    const result = await listAll(folderRef);
    
    const folders: FolderInfo[] = [];
    
    // Process subfolders
    for (const folder of result.prefixes) {
      const folderName = folder.name;
      const subfolderRef = ref(storage, `${fullPath}/${folderName}`);
      const subfolderResult = await listAll(subfolderRef);
      
      folders.push({
        name: folderName,
        path: `${folderPath}/${folderName}`.replace(/^\//, ''),
        files: subfolderResult.items.length,
        subfolders: subfolderResult.prefixes.map(p => p.name)
      });
    }
    
    return folders;
  } catch (error) {
    console.error('Error getting folder contents:', error);
    return [];
  }
};

// Get resident documents
export const getResidentDocuments = async (workspaceId: string): Promise<DocumentsResponse> => {
  if (isDemoWorkspace({ workspace: { id: workspaceId } } as any)) {
    const mockDocuments = await loadMockData<ResidentDocument[]>('residentDocuments', 'en');
    return {
      data: mockDocuments || [],
      total: mockDocuments?.length || 0,
      page: 1,
      pageSize: mockDocuments?.length || 0,
    };
  }

  // TODO: Implement Firestore API call
  return {
    data: [],
    total: 0,
    page: 1,
    pageSize: 10,
  };
};

// Get employee documents
export const getEmployeeDocuments = async (workspaceId: string): Promise<DocumentsResponse> => {
  if (isDemoWorkspace({ workspace: { id: workspaceId } } as any)) {
    // For demo, we'll use general documents and filter by employeeId
    const mockDocuments = await loadMockData<Document[]>('residentDocuments', 'en');
    const employeeDocuments = mockDocuments?.filter((doc: any) => doc.employeeId) || [];
    
    return {
      data: employeeDocuments,
      total: employeeDocuments.length,
      page: 1,
      pageSize: employeeDocuments.length,
    };
  }

  // TODO: Implement Firestore API call
  return {
    data: [],
    total: 0,
    page: 1,
    pageSize: 10,
  };
};

// Get administration documents
export const getAdministrationDocuments = async (params: DocumentsQueryParams): Promise<DocumentsResponse> => {
  if (isDemoWorkspace({ workspace: { id: params.workspaceId } } as any)) {
    const mockDocuments = await loadMockData<Document[]>('residentDocuments', 'en');
    const adminDocuments = mockDocuments?.filter((doc: any) => !doc.residentId && !doc.employeeId) || [];
    
    const paginatedData = paginateMockData(adminDocuments, params.pageSize, params.page);
    
    return {
      data: paginatedData.data,
      total: adminDocuments.length,
      page: params.page,
      pageSize: params.pageSize,
    };
  }

  // TODO: Implement Firestore API call
  return {
    data: [],
    total: 0,
    page: params.page,
    pageSize: params.pageSize,
  };
};

// Get documents by owner (resident or employee)
export const getDocumentsByOwner = async (params: DocumentsByOwnerParams): Promise<DocumentsResponse> => {
  if (isDemoWorkspace({ workspace: { id: params.workspaceId } } as any)) {
    let mockDocuments: Document[] = [];
    
    if (params.ownerType === 'resident') {
      const residentDocs = await loadMockData<ResidentDocument[]>('residentDocuments');
      mockDocuments = residentDocs?.filter((doc: any) => doc.residentId === params.ownerId) || [];
    } else {
      const generalDocs = await loadMockData<Document[]>('residentDocuments');
      mockDocuments = generalDocs?.filter((doc: any) => doc.employeeId === params.ownerId) || [];
    }
    
    const paginatedData = paginateMockData(mockDocuments, params.pageSize, params.page);
    
    return {
      data: paginatedData.data,
      total: mockDocuments.length,
      page: params.page,
      pageSize: params.pageSize,
    };
  }

  // TODO: Implement Firestore API call
  return {
    data: [],
    total: 0,
    page: params.page,
    pageSize: params.pageSize,
  };
};

// Enhanced document upload with folder structure support
export const uploadDocumentToFolder = async (params: DocumentUploadParams): Promise<Document> => {
  try {
    const {
      file,
      workspaceSlug,
      folder,
      subfolder,
      ownerId,
      ownerType,
      category,
      description
    } = params;

    // Get workspace language from params or default to 'en'
    const language = params.language || 'en';
    
    const localizedFolderNames = getLocalizedFolderNames(language);
    const rootPath = workspaceSlug;
    
    // Find the localized name for the folder
    const folderIndex = FIRST_LEVEL_FOLDERS.indexOf(folder);
    if (folderIndex === -1) {
      throw new Error(`Invalid folder: ${folder}`);
    }
    
    const localizedFolder = localizedFolderNames[folderIndex];
    let filePath = `${rootPath}/${localizedFolder}`;
    
    // Add subfolder if specified
    if (subfolder) {
      filePath += `/${subfolder}`;
    }
    
    // Add owner-specific subfolder if ownerId is provided
    if (ownerId && ownerType) {
      filePath += `/${ownerId}`;
    }
    
    // Create the file reference
    const fileName = `${Date.now()}_${file.name}`;
    const fileRef = ref(storage, `${filePath}/${fileName}`);
    
    // Upload the file
    const snapshot = await uploadBytes(fileRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    // Create document metadata
    const document: Document = {
      id: `doc_${Date.now()}`,
      name: file.name,
      type: file.type,
      uploadDate: new Date(),
      expiryDate: undefined,
      status: 'active',
      fileUrl: downloadURL,
      description: description || '',
      category: category || '',
      folder: filePath,
    };

    return document;
  } catch (error) {
    console.error('Error uploading document:', error);
    throw error;
  }
};

// Legacy upload function for backward compatibility
export const uploadDocument = async (file: File, _ownerId: string, _ownerType: 'resident' | 'employee'): Promise<Document> => {
  // TODO: Implement actual file upload to Firebase Storage
  // For now, return a mock document
  const mockDocument: Document = {
    id: `doc_${Date.now()}`,
    name: file.name,
    type: file.type,
    category: '',
    folder: '',
    uploadDate: new Date(),
    expiryDate: undefined,
    status: 'active',
    fileUrl: URL.createObjectURL(file),
    description: '',
  };

  return mockDocument;
};

// Delete document
export const deleteDocument = async (): Promise<void> => {
  // TODO: Implement Firestore document deletion and Firebase Storage file deletion
  throw new Error('Delete not implemented yet');
};

// Update document
export const updateDocument = async (): Promise<Document> => {
  // TODO: Implement Firestore document update
  throw new Error('Update not implemented yet');
};
